// @flow

import * as React from 'react';

import { useField, useForm } from 'react-final-form';

import {
  ChoiceField,
  ChoiceFieldGroup,
  useBasicValidators,
} from '@gandi/form/FinalForm';
import type {
  CertificateTypeOption,
  CertificateProtectOption,
} from '@gandi/redux-resource-price';
import { useTranslator } from '@gandi/translate';
import { Alert, Base, Block, BlockContent, BlockHeading } from '@gandi/ui';

import { useAppSelector } from 'src/hooks/useAppSelector';

import { selectAdminShsUrl } from '../../../SimpleHosting/modules/urls';
import Price from '../../../shared/Price';

import {
  CERT_ALTNAMES_INPUT_NAME,
  CERT_NUMBER_ADDRESSES_INPUT_NAME,
  CERT_PROVIDER_INPUT_NAME,
  CERT_TYPE_INPUT_NAME,
  CERT_TYPE_PROTECT_INPUT_NAME,
  choiceGroupProps,
} from '../../consts';

import { useTypePrices } from '../../hooks/prices';
import type { FormValues } from '../../types';

const mapping: {
  [type: CertificateTypeOption]: Array<CertificateProtectOption>,
  ...
} = {
  standard: ['single', 'multi', 'wildcard'],
  pro: ['single', 'wildcard'],
  business: ['single', 'multi'],
};

type Items = Array<{
  value: CertificateProtectOption,
  label: string,
  additional: React.MixedElement,
}>;
/**
 * compute available values even in between renders cf. `form.subscribe` below
 */
const computeAvailableValues = ({
  items,
  type,
  isSHS,
}: {
  items: Items,
  type: ?CertificateTypeOption,
  isSHS: boolean,
  process?: 'create' | 'renew',
}) =>
  items
    .filter(item => ((type && mapping?.[type]) || []).includes(item.value))
    // remove standard / single for hosting (cf. `baseItems`)
    .filter(item => !isSHS || item.value !== 'single' || type !== 'standard')
    .map(item => item.value);

export type TypeProtectOptionProps = {
  readOnly?: boolean,
  process: 'create' | 'renew',
  isSHS?: boolean,
};
export function TypeProtectOption({
  readOnly,
  process,
  isSHS = false,
}: TypeProtectOptionProps): React.MixedElement {
  const { __ } = useTranslator();
  const { required } = useBasicValidators();
  const form = useForm<FormValues>();

  const hostingUrl = useAppSelector(selectAdminShsUrl);

  const {
    input: { value: provider },
    // $FlowFixMe[prop-missing]
  } = useField(CERT_PROVIDER_INPUT_NAME, { subscription: { value: true } });
  const {
    input: { value: type },
    // $FlowFixMe[prop-missing]
  } = useField(CERT_TYPE_INPUT_NAME, { subscription: { value: true } });
  const {
    input: { value: protect },
    // $FlowFixMe[prop-missing]
  } = useField(CERT_TYPE_PROTECT_INPUT_NAME, { subscription: { value: true } });

  const { priceSingle, priceMulti, priceWildcard } = useTypePrices({
    provider,
    type,
    process,
  });

  const helpMap = {
    single: __('For example, protect example.com and www.example.com'),
    multi: __(
      'For example, protect addresses on more than one domain: www.example.com, blog.example.net, www.example.org...',
    ),
    wildcard: __(
      'Protect the domain and all its subdomains, for example www.example.com, blog.example.com, admin.example.com, anything.example.com...',
    ),
  };

  /**
   * According to the type of cert, available options are different:
   * - single / multi / wildcard for standard create
   * - multi / wildcard for standard create shs
   * - single / wildcard for pro
   * - single / multi for business
   */
  const baseItems: Items = [
    {
      value: 'single',
      label: __('Single address'),
      additional: (
        <>
          <div>{__('Only 1 address')}</div>
          {!!priceSingle && (
            <>
              {__('From')}
              <Price {...priceSingle} />
            </>
          )}
        </>
      ),
    },
    {
      value: 'multi',
      label: __('Several addresses'),
      additional: (
        <>
          <div>{__('From 3 to 20 protected addresses')}</div>
          {!!priceMulti && (
            <>
              {__('From')}
              <Price {...priceMulti} />
            </>
          )}
        </>
      ),
    },
    {
      value: 'wildcard',
      label: __('Full domain'),
      additional: (
        <>
          <div>{__('Unlimited subdomains')}</div>
          {!!priceWildcard && (
            <>
              {__('From')}
              <Price {...priceWildcard} />
            </>
          )}
        </>
      ),
    },
  ];

  const availableItemValues = computeAvailableValues({
    items: baseItems,
    type,
    isSHS,
  });
  const items = baseItems.filter(item =>
    availableItemValues.includes(item.value),
  );

  React.useEffect(() => {
    let previousValues: void | FormValues;

    const unsubscribe = form.subscribe(
      ({ values }) => {
        if (
          values?.[CERT_TYPE_PROTECT_INPUT_NAME] &&
          previousValues?.[CERT_TYPE_PROTECT_INPUT_NAME] ===
            values[CERT_TYPE_PROTECT_INPUT_NAME]
        ) {
          // first render & type_protect already set
          return;
        }

        // -- Type protection
        form.batch(() => {
          let nextProtect = values?.[CERT_TYPE_PROTECT_INPUT_NAME];
          if (!nextProtect) {
            // init
            nextProtect = computeAvailableValues({
              items: baseItems,
              type: values?.[CERT_TYPE_INPUT_NAME],
              process,
              isSHS,
            })[0];
            form.change(CERT_TYPE_PROTECT_INPUT_NAME, nextProtect);
          }

          if (nextProtect !== 'multi') {
            // cleanup SAN
            form.change(CERT_ALTNAMES_INPUT_NAME, undefined);
            form.change(CERT_NUMBER_ADDRESSES_INPUT_NAME, undefined);
          }
        });
        previousValues = values;
      },
      { values: true },
    );

    return unsubscribe;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const help = helpMap[protect];

  return (
    <>
      <Block>
        <BlockHeading>{__('What do you want to protect?')}</BlockHeading>
        <BlockContent>
          <Base marginBottom="small">
            <ChoiceFieldGroup
              name={CERT_TYPE_PROTECT_INPUT_NAME}
              validate={required}
              choiceGroupProps={choiceGroupProps}
            >
              {items.map(({ additional, label, value }) => (
                <ChoiceField
                  key={value}
                  name={CERT_TYPE_PROTECT_INPUT_NAME}
                  value={value}
                  label={label}
                  disabled={readOnly}
                >
                  {additional && <div slot="additional">{additional}</div>}
                </ChoiceField>
              ))}
            </ChoiceFieldGroup>
          </Base>
          <Base tagName="p" margin="none">
            {help}
          </Base>
        </BlockContent>
      </Block>
      {isSHS && type === 'standard' && (
        <Alert
          title={__('Where is my «Standard single address certificate»?!')}
          message={__(
            'Actually it is free, you can enable it on your <a href="%(hostingUrl)s">web hosting administration</a>',
            { hostingUrl, withHTML: true },
          )}
          intent="info"
          marginTop="small"
        />
      )}
    </>
  );
}
